import { client, gql } from 'data-provider';
import { IPaymentLog, IPaymentMethod } from 'interfaces';
import { EUserType } from 'pages/accounts/logs.type';
import { capitalize } from 'utilities/shared';

export const getPaymentMethods = async (
  resource: 'client' | 'supplier' | 'company',
  resourceId: string
) => {
  const capitalizedResource = capitalize(resource);
  const key = `${capitalizedResource}PaymentMethods`;

  const query = gql`
    query ($id: ID!) {
      ${key}(where: { ${resource.toLowerCase()}: $id }) {
        id
        accountName
        accountNumber
        bankName
        branchName
        swiftCode
      }
    }
  `;

  const variables = { id: resourceId };
  const data = await client.request(query, variables);
  return data[key] as IPaymentMethod[];
};

export const createPaymentMethod = async (resource: string, data: Omit<IPaymentMethod, 'id'>) => {
  const capitalizedResource = capitalize(resource);

  const createPaymentMethod = gql`
    mutation ($input: create${capitalizedResource}PayMethodInput) {
      create${capitalizedResource}PaymentMethod(input: $input) {
        id
        accountName
        accountNumber
        bankName
        branchName
        swiftCode
      }
    }
  `;

  const variables = { input: { data } };
  const createdData = await client.request(createPaymentMethod, variables);
  return createdData;
};

export const updatePaymentMethod = async (
  resource: string,
  resourceId: string,
  data: Omit<IPaymentMethod, 'id'>
) => {
  const capitalizedResource = capitalize(resource);

  const updatePaymentMethod = gql`
    mutation ($input: update${capitalizedResource}PayMethodInput) {
      update${capitalizedResource}PaymentMethod(input: $input) {
        ${resource}PaymentMethod {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id: resourceId },
      data,
    },
  };

  const updatedData = await client.request(updatePaymentMethod, variables);
  return updatedData;
};

export const getPaymentLogs = async (resource: 'sales' | 'purchase', resourceId: string) => {
  const lowercaseResource = resource.toLowerCase();
  const resourceName = `${lowercaseResource}PaymentLogsCopy`;
  const queryKey = resource === 'sales' ? 'salesCopy' : 'purchaseCopy';

  const query = gql`
    query ($where: JSON, $sort: SortInput) {
      ${resourceName}(where: $where, sort: $sort) {
        id
        paymentDate
        paymentDetails
        paymentType
        currentPaidAmount
        additionalInfo
        paymentMethod {
          id
          accountName
          accountNumber
          bankName
          branchName
          swiftCode
        }
        attachments {
          id
          attachment {
            uid
            name
            url
            thumbUrl
            size
            type
            lastModifiedDate
          }
        }
        companyBank {
          id
          accountName
          accountNumber
          bankName
          branchName
          swiftCode
        }
      }
    }
  `;

  const variables = {
    where: {
      [queryKey]: resourceId,
    },
  };
  const data = await client.request(query, variables);
  return data[resourceName] as IPaymentLog[];
};

export interface ICreatePaymentLogData {
  paymentType: string;
  paymentMethod?: string;
  currentPaidAmount: number;
  paymentDate: Date;
  paymentDetails?: string;
  attachments?: any[];
  sales?: string;
  purchase?: string;
}

export const createPaymentLog = async (
  resource: 'sale' | 'purchase',
  data: ICreatePaymentLogData
) => {
  const capitalizedResource = capitalize(resource);

  const createPaymentLog = gql`
    mutation ($input: create${capitalizedResource}PaymentCopyInput!) {
      create${capitalizedResource}PaymentLogCopy(input: $input) {
        id
      }
    }
  `;

  const variables = { input: { data } };
  const createdData = await client.request(createPaymentLog, variables);
  return createdData;
};

export const updatePaymentLog = async (
  resource: 'sale' | 'purchase',
  paymentId: string,
  data: any
) => {
  const capitalizedResource = capitalize(resource);

  const updatePaymentLog = gql`
    mutation ($input: update${capitalizedResource}PaymentLogCopyInput!) {
      update${capitalizedResource}PaymentLogCopy(input: $input) {
        ${resource === 'sale' ? 'sales' : 'purchase'}PaymentLogCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id: paymentId },
      data,
    },
  };

  const updatedData = await client.request(updatePaymentLog, variables);
  return updatedData;
};

export interface IMakePaymentParams {
  userType: 'client' | 'supplier';
  id: string;
  paymentType: string;
  paymentMethod?: string;
  currentPaidAmount: number;
  companyBank: string;
  paymentDate: Date;
  paymentDetails?: string;
  attachments?: any[];
}

export const makePayment = async (paymentParam: IMakePaymentParams) => {
  const {
    userType,
    id,
    paymentType,
    paymentMethod,
    currentPaidAmount,
    companyBank,
    paymentDate,
    paymentDetails,
    attachments,
  } = paymentParam;

  const makePayment = gql`
    mutation ($input: makePaymentDto!) {
      makePayments(input: $input) {
        ledgerPaymentId
      }
    }
  `;

  const variables = {
    input: {
      userType,
      id,
      paymentType,
      paymentMethod,
      currentPaidAmount,
      companyBank,
      paymentDate,
      paymentDetails,
      attachments,
    },
  };

  const paymentData = await client.request(makePayment, variables);
  return paymentData.makePayments.ledgerPaymentId as string;
};

export const deletePaymentMethod = async (id: string, resource: string) => {
  const capitalizedResource = capitalize(resource);

  const deletePaymentMethodQuery = gql`
    mutation ($input: delete${capitalizedResource}PaymentMethodInput) {
      delete${capitalizedResource}PaymentMethod(input: $input) {
        ${resource}PaymentMethod {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deletePaymentMethodQuery, variables);
};

export const getPaymentReceipt = async (
  id: string,
  userType: EUserType,
  runningBalance: number
) => {
  const query = gql`
    query Query($input: accountInvoicePdfDto!) {
      generateAccountInvoicePdf(input: $input)
    }
  `;

  const variables = {
    input: {
      invoiceId: id,
      runningBalance,
      userType,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
  const data = await client.request(query, variables);
  return data.generateAccountInvoicePdf as string;
};
